import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  FormControl,
  InputLabel,
  Select,
  Tab,
  Tabs,
  LinearProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Download as ExportIcon,
  LocalPharmacy as PrescriptionIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  CheckCircle as CompletedIcon,
  Pending as PendingIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingIcon,
  MedicalServices as MedicalIcon,
  Print as PrintIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { RootState } from '../../store';
import axios from 'axios';

// Types
interface PrescriptionListItem {
  id: number;
  prescriptionNumber: string;
  prescribedAt: string;
  status: string;
  statusColor: string;
  patientId: number;
  patientName: string;
  patientType: string;
  patientMobile: string;
  doctorId: number;
  doctorName: string;
  doctorSpecialization: string;
  bookingNumber: string;
  medicationCount: number;
  diagnosis: string;
  notes: string;
  followUpRequired: boolean;
  followUpDate: string | null;
  createdAt: string;
}

interface PrescriptionStatistics {
  totalToday: number;
  pendingToday: number;
  completedToday: number;
  followUpDue: number;
  totalPrescriptions: number;
  pendingPrescriptions: number;
  completedPrescriptions: number;
  withFollowUp: number;
  statusDistribution: { status: string; displayName: string; count: number; color: string }[];
  topMedications: { medicationName: string; count: number; category: string }[];
  topDoctors: { doctorId: number; doctorName: string; specialization: string; prescriptionCount: number }[];
}

const MedicalRecordsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);

  const [loading, setLoading] = useState(true);
  const [prescriptions, setPrescriptions] = useState<PrescriptionListItem[]>([]);
  const [statistics, setStatistics] = useState<PrescriptionStatistics | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [actionAnchorEl, setActionAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPrescription, setSelectedPrescription] = useState<PrescriptionListItem | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  const fetchPrescriptions = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const params = new URLSearchParams({
        page: page.toString(),
        size: rowsPerPage.toString(),
        search: searchQuery,
        status: statusFilter,
      });

      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/v1/admin/medical-records/prescriptions?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setPrescriptions(response.data.data.content);
        setTotalElements(response.data.data.totalElements);
      }
    } catch (error) {
      console.error('Error fetching prescriptions:', error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchQuery, statusFilter]);

  const fetchStatistics = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/v1/admin/medical-records/prescriptions/statistics`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setStatistics(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  }, []);

  useEffect(() => {
    fetchPrescriptions();
    fetchStatistics();
  }, [fetchPrescriptions, fetchStatistics]);

  const handleActionOpen = (event: React.MouseEvent<HTMLElement>, prescription: PrescriptionListItem) => {
    setActionAnchorEl(event.currentTarget);
    setSelectedPrescription(prescription);
  };

  const handleActionClose = () => {
    setActionAnchorEl(null);
    setSelectedPrescription(null);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'CLOSED': return 'Completed';
      case 'VISITED': return 'Pending';
      default: return status;
    }
  };

  const statCards = statistics ? [
    { title: "Today's Prescriptions", value: statistics.totalToday, icon: <PrescriptionIcon />, color: '#2563eb' },
    { title: 'Pending', value: statistics.pendingToday, icon: <PendingIcon />, color: '#f59e0b' },
    { title: 'Completed', value: statistics.completedToday, icon: <CompletedIcon />, color: '#10b981' },
    { title: 'Follow-up Due', value: statistics.followUpDue, icon: <WarningIcon />, color: '#ef4444' },
    { title: 'Total Prescriptions', value: statistics.totalPrescriptions, icon: <MedicalIcon />, color: '#8b5cf6' },
    { title: 'With Follow-up', value: statistics.withFollowUp, icon: <TimeIcon />, color: '#06b6d4' },
  ] : [];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
            Medical Records & Prescriptions
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
            Monitor prescriptions, medication history, and patient records
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => { fetchPrescriptions(); fetchStatistics(); }}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.75rem' }}>
                      {stat.title}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: stat.color, mt: 0.5 }}>
                      {stat.value}
                    </Typography>
                  </Box>
                  <Box sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: `${stat.color}15`,
                    color: stat.color,
                  }}>
                    {stat.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Top Medications */}
      {statistics && statistics.topMedications.length > 0 && (
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              Top Prescribed Medications
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {statistics.topMedications.map((med, index) => (
                <Chip
                  key={index}
                  label={`${med.medicationName}: ${med.count}`}
                  size="small"
                  sx={{
                    borderRadius: 1,
                    fontWeight: 500,
                    bgcolor: 'transparent' ,
                    color: '#475569',
                  }}
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Top Doctors */}
      {statistics && statistics.topDoctors.length > 0 && (
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              Top Prescribing Doctors
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {statistics.topDoctors.map((doc, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'transparent' , fontSize: '0.75rem' }}>
                    {doc.doctorName?.charAt(0) || 'D'}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {doc.doctorName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      {doc.prescriptionCount} prescriptions
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Paper sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: '1px solid #e2e8f0' }}>
          <Tab label={`All (${totalElements})`} sx={{ textTransform: 'none' }} />
          <Tab label="Pending" sx={{ textTransform: 'none' }} />
          <Tab label="Completed" sx={{ textTransform: 'none' }} />
          <Tab label="Follow-up Due" sx={{ textTransform: 'none' }} />
        </Tabs>

        {/* Search & Filter */}
        <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
          <TextField
            size="small"
            placeholder="Search prescriptions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#64748b' }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="COMPLETED">Completed</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'transparent' }}>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>RX #</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Patient</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Doctor</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Diagnosis</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Meds</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Follow-up</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              ) : prescriptions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <Typography sx={{ color: '#64748b' }}>No prescriptions found</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                prescriptions.map((prescription) => (
                  <TableRow key={prescription.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {prescription.prescriptionNumber}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        {prescription.bookingNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {prescription.prescribedAt ? new Date(prescription.prescribedAt).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'transparent' , fontSize: '0.75rem' }}>
                          {prescription.patientName?.charAt(0) || 'P'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {prescription.patientName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            {prescription.patientType}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'transparent' , fontSize: '0.75rem' }}>
                          {prescription.doctorName?.charAt(0) || 'D'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {prescription.doctorName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            {prescription.doctorSpecialization}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {prescription.diagnosis || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${prescription.medicationCount} meds`}
                        size="small"
                        sx={{ borderRadius: 1, fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(prescription.status)}
                        size="small"
                        sx={{
                          bgcolor: `${prescription.statusColor}15`,
                          color: prescription.statusColor,
                          fontWeight: 500,
                          fontSize: '0.7rem',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {prescription.followUpRequired ? (
                        <Chip
                          label={prescription.followUpDate ? new Date(prescription.followUpDate).toLocaleDateString() : 'Yes'}
                          size="small"
                          sx={{ bgcolor: '#f59e0b15', color: '#f59e0b', fontWeight: 500, fontSize: '0.7rem' }}
                        />
                      ) : (
                        <Typography variant="caption" sx={{ color: '#64748b' }}>No</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={(e) => handleActionOpen(e, prescription)}>
                        <TimeIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalElements}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={actionAnchorEl}
        open={Boolean(actionAnchorEl)}
        onClose={handleActionClose}
      >
        <MenuItem onClick={handleActionClose}>
          <ListItemIcon><ViewIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleActionClose}>
          <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View Patient</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleActionClose}>
          <ListItemIcon><MedicalIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Medication History</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleActionClose}>
          <ListItemIcon><PrintIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Print Prescription</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default MedicalRecordsPage;